import { pgTable, serial, integer, text, timestamp, boolean, decimal, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Compliance Checklist Items - NEW structured checklist per area
export const complianceChecklistItems = pgTable("compliance_checklist_items", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  areaKey: text("area_key").notNull(), // cobra, hipaa, erisa, aca, fmla
  title: text("title").notNull(),
  description: text("description"),
  status: text("status").notNull().default("missing"), // missing, pending, review_needed, action_needed, completed
  formType: text("form_type"), // area-specific form type
  isRequired: boolean("is_required").default(true), // true = required, false = suggested
  dueDate: timestamp("due_date"),
  assignedToUserId: text("assigned_to_user_id"),
  notes: text("notes"),
  sortOrder: integer("sort_order").default(0),
  reviewFrequency: text("review_frequency").default("none"), // none, annual
  lastResetDate: timestamp("last_reset_date"), // when the item was last reset
  nextResetDate: timestamp("next_reset_date"), // when the item should be reset (Jan 1 or plan renewal)
  resetMonth: integer("reset_month"), // 1-12, null = use client default or January
  createdByUserId: text("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Compliance Evidence - files or links attached to checklist items
export const complianceEvidence = pgTable("compliance_evidence", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  checklistItemId: integer("checklist_item_id"), // nullable for misc docs
  type: text("type").notNull(), // file, link
  fileName: text("file_name"), // original file name
  documentName: text("document_name"), // user-friendly display name
  fileType: text("file_type"), // sbc, spd, form5500, wrap_document, hipaa_notice, cobra_notice, etc.
  fileUrl: text("file_url"),
  linkUrl: text("link_url"),
  s3Key: text("s3_key"),
  visibility: text("visibility").notNull().default("internal"), // internal, employee
  uploadedByUserId: text("uploaded_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Compliance Templates - quick templates for download/link
export const complianceTemplates = pgTable("compliance_templates", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  areaKey: text("area_key").notNull(), // cobra, hipaa, erisa, aca, general
  type: text("type").notNull(), // download, link
  fileUrl: text("file_url"),
  linkUrl: text("link_url"),
  description: text("description"),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

// Compliance Audit Log - track all compliance actions
export const complianceAuditLog = pgTable("compliance_audit_log", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id"),
  entityType: text("entity_type").notNull(), // checklist_item, evidence, template, aca_form
  entityId: integer("entity_id"),
  action: text("action").notNull(), // create, update, delete, download, view
  metadata: jsonb("metadata").default({}),
  actorUserId: text("actor_user_id"),
  actorEmail: text("actor_email"),
  ipAddress: text("ip_address"),
  createdAt: timestamp("created_at").defaultNow(),
});

// High-level arbitrary compliance items an employer tracks (legacy - keep for backward compat)
export const complianceItems = pgTable("compliance_items", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  title: text("title").notNull(),
  category: text("category").notNull().default("general"), // cobra, hipaa, erisa, aca, general
  status: text("status").notNull().default("open"), // open, in_progress, completed
  dueDate: timestamp("due_date"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// COBRA Participants (derived from former employees + enrollment data)
export const cobraParticipants = pgTable("cobra_participants", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(),
  clientId: integer("client_id").notNull(),
  qualifyingEvent: text("qualifying_event").notNull(), // termination, reduction_hours, divorce, death, loss_dependent_status
  eventDate: timestamp("event_date").notNull(),
  electionDeadline: timestamp("election_deadline"),
  coverageEndDate: timestamp("coverage_end_date"),
  coverageType: text("coverage_type"), // employee_only, employee_spouse, employee_children, family
  monthlyPremium: decimal("monthly_premium", { precision: 10, scale: 2 }),
  elected: boolean("elected").default(false),
  paymentStatus: text("payment_status").default("current"), // current, late, grace, defaulted
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// COBRA Notices tracking (initial, election, premium etc.)
export const cobraNotices = pgTable("cobra_notices", {
  id: serial("id").primaryKey(),
  participantId: integer("participant_id").notNull(),
  type: text("type").notNull(), // initial, election, premium
  sent: boolean("sent").default(false),
  sentDate: timestamp("sent_date"),
  method: text("method"), // mail, email, portal
  createdAt: timestamp("created_at").defaultNow(),
});

// COBRA Payments minimal (track status changes lightly)
export const cobraPayments = pgTable("cobra_payments", {
  id: serial("id").primaryKey(),
  participantId: integer("participant_id").notNull(),
  month: text("month").notNull(), // YYYY-MM
  amountDue: decimal("amount_due", { precision: 10, scale: 2 }).notNull(),
  amountPaid: decimal("amount_paid", { precision: 10, scale: 2 }).default("0"),
  paidDate: timestamp("paid_date"),
  status: text("status").notNull().default("due"), // due, partial, paid, late
  createdAt: timestamp("created_at").defaultNow(),
});

// HIPAA/ERISA checklist confirmations per client (legacy)
export const complianceChecklists = pgTable("compliance_checklists", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  type: text("type").notNull(), // hipaa | erisa
  checks: jsonb("checks").notNull().default({}),
  confirmedBy: text("confirmed_by"),
  confirmedAt: timestamp("confirmed_at"),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Insert schemas
export const insertComplianceChecklistItemSchema = createInsertSchema(complianceChecklistItems)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    dueDate: z.string().optional().or(z.date().optional()).nullable(),
  });

export const insertComplianceEvidenceSchema = createInsertSchema(complianceEvidence)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertComplianceTemplateSchema = createInsertSchema(complianceTemplates)
  .omit({ id: true, createdAt: true });

export const insertComplianceAuditLogSchema = createInsertSchema(complianceAuditLog)
  .omit({ id: true, createdAt: true });

export const insertComplianceItemSchema = createInsertSchema(complianceItems)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    dueDate: z.string().optional().or(z.date().optional()),
  });

export const insertCobraParticipantSchema = createInsertSchema(cobraParticipants).omit({ id: true, createdAt: true, updatedAt: true });
export const insertCobraNoticeSchema = createInsertSchema(cobraNotices).omit({ id: true, createdAt: true });
export const insertCobraPaymentSchema = createInsertSchema(cobraPayments).omit({ id: true, createdAt: true });
export const insertComplianceChecklistSchema = createInsertSchema(complianceChecklists)
  .omit({ id: true, createdAt: true, updatedAt: true });

// Types
export type ComplianceChecklistItem = typeof complianceChecklistItems.$inferSelect;
export type InsertComplianceChecklistItem = z.infer<typeof insertComplianceChecklistItemSchema>;
export type ComplianceEvidence = typeof complianceEvidence.$inferSelect;
export type InsertComplianceEvidence = z.infer<typeof insertComplianceEvidenceSchema>;
export type ComplianceTemplate = typeof complianceTemplates.$inferSelect;
export type InsertComplianceTemplate = z.infer<typeof insertComplianceTemplateSchema>;
export type ComplianceAuditLog = typeof complianceAuditLog.$inferSelect;
export type InsertComplianceAuditLog = z.infer<typeof insertComplianceAuditLogSchema>;
export type ComplianceItem = typeof complianceItems.$inferSelect;
export type InsertComplianceItem = z.infer<typeof insertComplianceItemSchema>;
export type CobraParticipant = typeof cobraParticipants.$inferSelect;
export type InsertCobraParticipant = z.infer<typeof insertCobraParticipantSchema>;
export type CobraNotice = typeof cobraNotices.$inferSelect;
export type InsertCobraNotice = z.infer<typeof insertCobraNoticeSchema>;
export type CobraPayment = typeof cobraPayments.$inferSelect;
export type InsertCobraPayment = z.infer<typeof insertCobraPaymentSchema>;
export type ComplianceChecklist = typeof complianceChecklists.$inferSelect;
export type InsertComplianceChecklist = z.infer<typeof insertComplianceChecklistSchema>;

// Default checklist items to seed per area
export const DEFAULT_CHECKLIST_ITEMS: Record<string, Array<{ title: string; description?: string; formType?: string; isRequired?: boolean; reviewFrequency?: 'none' | 'annual' }>> = {
  cobra: [
    { title: "COBRA Administrator Assigned", description: "Designate internal or TPA administrator for COBRA", isRequired: true, reviewFrequency: 'annual' },
    { title: "Initial Notice Procedure", description: "Process for sending initial COBRA notice to new employees", isRequired: true, reviewFrequency: 'annual' },
    { title: "Election Notice", description: "Company's initial election notice for employee access", formType: "election_notice", isRequired: true, reviewFrequency: 'annual' },
    { title: "Premium Calculation Method", description: "Document how COBRA premiums are calculated (102% of cost)", isRequired: true, reviewFrequency: 'annual' },
    { title: "Payment Tracking Process", description: "System for tracking COBRA premium payments", isRequired: true, reviewFrequency: 'annual' },
    { title: "Qualifying Event Procedures", description: "Documented procedures for handling each qualifying event type", isRequired: true, reviewFrequency: 'annual' },
    { title: "COBRA Notification Log", description: "Log to track all COBRA notices sent with dates and methods", isRequired: false },
    { title: "Premium Payment Grace Period Policy", description: "Written policy for 30-day grace period handling", isRequired: false },
  ],
  hipaa: [
    { title: "Notice of Privacy Practices", description: "NPP distributed to all plan participants", formType: "npp", isRequired: true, reviewFrequency: 'annual' },
    { title: "Business Associate Agreements", description: "BAAs in place with all vendors handling PHI", formType: "baa", isRequired: true, reviewFrequency: 'annual' },
    { title: "Privacy Officer Designated", description: "Named individual responsible for HIPAA compliance", isRequired: true, reviewFrequency: 'annual' },
    { title: "Administrative Safeguards", description: "Policies and procedures for PHI handling", isRequired: true, reviewFrequency: 'annual' },
    { title: "Physical Safeguards", description: "Physical security measures for PHI", isRequired: true, reviewFrequency: 'annual' },
    { title: "Technical Safeguards", description: "Electronic PHI security measures", isRequired: true, reviewFrequency: 'annual' },
    { title: "Risk Assessment Completed", description: "Annual HIPAA risk assessment conducted", formType: "risk_assessment", isRequired: false, reviewFrequency: 'annual' },
    { title: "Breach Notification Procedures", description: "Written procedures for breach notification", isRequired: false },
  ],
  erisa: [
    { title: "Plan Document", description: "Written plan document on file", formType: "plan_document", isRequired: true, reviewFrequency: 'annual' },
    { title: "Summary Plan Description (SPD)", description: "SPD distributed to participants", formType: "spd", isRequired: true, reviewFrequency: 'annual' },
    { title: "Summary of Benefits and Coverage (SBC)", description: "SBC provided for health plans", formType: "sbc", isRequired: true, reviewFrequency: 'annual' },
    { title: "Wrap Document", description: "Wrap document combining multiple plans if applicable", formType: "wrap_document", isRequired: false, reviewFrequency: 'annual' },
    { title: "Form 5500 Filing", description: "Annual Form 5500 filed (if applicable)", formType: "form_5500", isRequired: true, reviewFrequency: 'annual' },
    { title: "Fiduciary Responsibilities", description: "Named fiduciary and documented responsibilities", isRequired: true, reviewFrequency: 'annual' },
    { title: "Claims and Appeals Procedures", description: "Written procedures for claims and appeals", isRequired: false },
  ],
  aca: [
    { title: "ALE Status Determination", description: "Determine if employer is an Applicable Large Employer", isRequired: true, reviewFrequency: 'annual' },
    { title: "Measurement Method Selected", description: "Monthly or look-back measurement method documented", isRequired: true, reviewFrequency: 'annual' },
    { title: "Affordability Testing", description: "Annual affordability calculation completed", isRequired: true, reviewFrequency: 'annual' },
    { title: "Minimum Value Plans", description: "Health plans meet minimum value requirements", isRequired: true, reviewFrequency: 'annual' },
    { title: "1095-C Forms Prepared", description: "Employee 1095-C forms ready for distribution", formType: "1095c", isRequired: true, reviewFrequency: 'annual' },
    { title: "1094-C Transmittal", description: "Employer transmittal form prepared", formType: "1094c", isRequired: true, reviewFrequency: 'annual' },
    { title: "Safe Harbor Documentation", description: "Safe harbor method documented if applicable", isRequired: false, reviewFrequency: 'annual' },
  ],
  fmla: [
    { title: "FMLA Eligibility Criteria Posted", description: "Eligibility requirements posted in workplace", isRequired: true, reviewFrequency: 'annual' },
    { title: "FMLA Poster Displayed", description: "Required FMLA poster displayed in conspicuous location", isRequired: true, reviewFrequency: 'annual' },
    { title: "Leave Request Forms", description: "Standard forms for FMLA leave requests", formType: "leave_request", isRequired: true },
    { title: "Medical Certification Forms", description: "Forms for medical certification of serious health condition", formType: "medical_cert", isRequired: true },
    { title: "Eligibility Notice Template", description: "Template for notifying employees of FMLA eligibility", isRequired: true },
    { title: "Rights and Responsibilities Notice", description: "Notice outlining employee rights and responsibilities", isRequired: true },
    { title: "Designation Notice Template", description: "Template for designating leave as FMLA-qualifying", isRequired: true },
    { title: "FMLA Policy in Handbook", description: "Comprehensive FMLA policy included in employee handbook", isRequired: false, reviewFrequency: 'annual' },
    { title: "Leave Tracking System", description: "System for tracking FMLA leave balances and usage", isRequired: false },
    { title: "Return to Work Procedures", description: "Procedures for employee return from FMLA leave", isRequired: false },
  ],
};

// Default templates to seed
export const DEFAULT_TEMPLATES: Array<{
  title: string;
  areaKey: string;
  type: "download" | "link";
  linkUrl?: string;
  description?: string;
}> = [
  {
    title: "COBRA Initial/General Notice (DOL Model)",
    areaKey: "cobra",
    type: "link",
    linkUrl: "https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/publications/model-general-notice.pdf",
    description: "Department of Labor model general notice",
  },
  {
    title: "COBRA Election Notice (DOL Model)",
    areaKey: "cobra",
    type: "link",
    linkUrl: "https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/publications/model-election-notice.pdf",
    description: "Department of Labor model election notice",
  },
  {
    title: "HIPAA Notice of Privacy Practices (HHS Sample)",
    areaKey: "hipaa",
    type: "link",
    linkUrl: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/model-notices-privacy-practices/index.html",
    description: "HHS model notice of privacy practices",
  },
  {
    title: "ERISA SPD Requirements Guide",
    areaKey: "erisa",
    type: "link",
    linkUrl: "https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/publications/reporting-and-disclosure-guide-for-employee-benefit-plans.pdf",
    description: "DOL guide on SPD requirements",
  },
  {
    title: "ACA 1095-C Instructions (IRS)",
    areaKey: "aca",
    type: "link",
    linkUrl: "https://www.irs.gov/instructions/i109495c",
    description: "IRS instructions for Form 1095-C",
  },
  {
    title: "FMLA Poster (DOL)",
    areaKey: "fmla",
    type: "link",
    linkUrl: "https://www.dol.gov/sites/dolgov/files/WHD/legacy/files/fmlaen.pdf",
    description: "Required FMLA workplace poster",
  },
  {
    title: "FMLA Forms (WH-380/381/382/384/385)",
    areaKey: "fmla",
    type: "link",
    linkUrl: "https://www.dol.gov/agencies/whd/fmla/forms",
    description: "DOL FMLA certification and notice forms",
  },
];
